import { useCallback, useEffect, useRef } from "react";

// AudioContext-based hook: evita el bloqueo de Autoplay Policy del navegador.
// El contexto se desbloquea en el primer click/keydown del usuario y queda
// activo para reproducción programática posterior (notificaciones push, etc).
export const useWithSound = (audioSource) => {
  const ctxRef = useRef(null);
  const bufferRef = useRef(null);

  const getCtx = () => {
    if (!ctxRef.current) {
      ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return ctxRef.current;
  };

  useEffect(() => {
    // Precarga el audio como buffer decodificado
    fetch(audioSource)
      .then((r) => r.arrayBuffer())
      .then((data) => getCtx().decodeAudioData(data))
      .then((buffer) => {
        bufferRef.current = buffer;
      })
      .catch(console.error);

    // Desbloquea el AudioContext en la primera interacción del usuario
    const unlock = () => {
      const ctx = getCtx();
      if (ctx.state === "suspended") ctx.resume();
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
    };
    window.addEventListener("click", unlock);
    window.addEventListener("keydown", unlock);

    return () => {
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, [audioSource]);

  const playSound = useCallback(() => {
    const ctx = getCtx();
    if (!bufferRef.current) return;
    if (ctx.state === "suspended") ctx.resume();
    const source = ctx.createBufferSource();
    source.buffer = bufferRef.current;
    source.connect(ctx.destination);
    source.start(0);
  }, []);

  const pauseSound = useCallback(() => {
    ctxRef.current?.suspend();
  }, []);

  return { playSound, pauseSound };
};
